import { Component, Injector, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { SortOrder } from 'src/app/core/interface/sort-order';
import { LaasOption } from 'src/app/core/models/laas-option';
import { CodeConverterOptions } from 'src/app/core/models/options/code-converter-options';
import { PageInfo } from 'src/app/core/models/page-info';
import { VMIAdvisingQueryResult } from 'src/app/core/models/transport-operation/vmi-advising-query-result';
import { CommonDataService } from 'src/app/core/services/common-data.service';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { FacilityService } from 'src/app/core/services/facility.service';
import { AddressMaintainanceService } from 'src/app/features/setting/service/address-maintainance.service';
import { AdvisingServiceService } from 'src/app/features/transport-operation/services/advising-service.service';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { InputValidators } from 'src/app/shared/validators/input-validators';
import { DeliveryOrderService } from '../../services/delivery-order.service';
import { PlantService } from '../../services/plant-service';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
@Component({
  selector: 'laas-vmi-advising',
  templateUrl: './vmi-advising.component.html',
  styleUrls: ['./vmi-advising.component.scss']
})
export class VmiAdvisingComponent extends SessionBaseComponent {
  readonly viewPermissionCode = PermissionConstants.VmiAdvising;
  @ViewChild('searchTable') searchTable!: DataTableComponent;
  currentLang: string = this.laasSetting.language;
  // loading
  pageLoading: boolean = false;
  tableLoading: boolean = false;
  uploadLoading: boolean = false;

  // dropdown options
  facilityOptions: LaasOption[] = [];
  plantOptions: LaasOption[] = [];
  storerOptions: LaasOption[] = [];
  vendorOptions: LaasOption[] = [];
  brandOptions: LaasOption[] = [];
  ambientStorageOptions: LaasOption[] = [];
  docStatusOptions: LaasOption[] = [];

  // search condition Accordion
  showConditionAccordion: boolean = true;


  // preference def
  preferenceKey: string = UserPreferenceConstants.VMIAdvisingSearchTable;
  detailPreferenceKey: string = UserPreferenceConstants.VMIAdvisingSearchTableDetail;

  // searchForm
  searchForm: FormGroup;

  // default pageable
  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
  };
  // default sort by SeqNo desc
  sortOrder: SortOrder = {
    property: 'SeqNo',
    direction: -1
  };

  tableColumn = [
    { field: 'adNo', header: 'VMIAdVising.Table.Column.AdNo', sort: true },
    { field: 'facility', header: 'VMIAdVising.Table.Column.Facility', sort: true },
    { field: 'vendorPlant', header: 'VMIAdVising.Table.Column.VendorPlant', sort: true },
    { field: 'vendorNo', header: 'VMIAdVising.Table.Column.Vendor', sort: true },
    { field: 'storer', header: 'VMIAdVising.Table.Column.Storer', sort: true },
    { field: 'adStatus', header: 'VMIAdVising.Table.Column.AdStatus', sort: true },
    { field: 'isBonded', header: 'VMIAdVising.Table.Column.IsBonded', sort: true },
    { field: 'billOfLading', header: 'VMIAdVising.Table.Column.BillOfLading', sort: true },
    { field: 'invoiceNo', header: 'VMIAdVising.Table.Column.InvoiceNo', sort: true },
    { field: 'createUser', header: 'VMIAdVising.Table.Column.CreateUser', sort: true },
    { field: 'createDt', header: 'VMIAdVising.Table.Column.CreateDt', dataType: 'date', sort: true },
    { field: 'updateUser', header: 'VMIAdVising.Table.Column.UpdateUser', sort: true },
    { field: 'updateDt', header: 'VMIAdVising.Table.Column.UpdateDt', dataType: 'date', sort: true },
  ];
  detailColumns = [
    { field: 'manufacturerPartNumber', header: 'VMIAdVising.Table.Column.ManufacturerPartNumber' },
    { field: 'sku', header: 'VMIAdVising.Table.Column.Sku' },
    { field: 'brand', header: 'VMIAdVising.Table.Column.Brand' },
    { field: 'expectedQty', header: 'VMIAdVising.Table.Column.ExpectedQty' },
    { field: 'ambientStorage', header: 'VMIAdVising.Table.Column.AmbientStorage' },
  ];
  searchResults: VMIAdvisingQueryResult[] = [];

  constructor(injector: Injector,
    private formBuilder: FormBuilder,
    private advisingService: AdvisingServiceService,
    private commonDataService: CommonDataService,
    private dropdownOptionsService: DropdownOptionsService,
    private addressMaintainanceService: AddressMaintainanceService,
    private plantService: PlantService,
    private facilityService: FacilityService,
    private inputValidators: InputValidators,
    private doService: DeliveryOrderService,
  ) {
    super(injector);

    // 搜尋條件初始化 & 驗證
    this.searchForm = this.formBuilder.group({
      facilityList: [[this.laasSetting.facility]],
      plantList: [[]],
      storerList: [[]],
      vendorList: [[], Validators.required],
      brandList: [[]],
      manufacturerPartNumber: [],
      sku: [],
    });

  }

  // 上傳、下載
  showUploadAdModal: boolean = false;
  selectedFile: File | null = null;

  async ngOnInit(): Promise<void> {

    // 取所有下拉單
    this.dropdownOptionsService.getFacilityOptions().subscribe({
      next: (result: { optionDesc: string; optionCode: string }[]) => {
        this.facilityOptions = result.map(
          (x) => ({
            label: x.optionDesc,
            value: x.optionCode,
          })
        );
      },
    });

    this.plantService.getVendorPlantOptions().subscribe({
      next: (plantList: { plantId: string }[]) => {
        const uniquePlantIds: string[] = Array.from(new Set(plantList.map((x) => x.plantId)));
        this.plantOptions = uniquePlantIds.map(plantId => ({
          label: plantId,
          value: plantId
        }));
      }
    });

    this.addressMaintainanceService.getStorerOptions().subscribe({
      next: (result: { optionCode: string }[]) => {
        this.storerOptions = result.map(
          (x) => ({
            label: x.optionCode,
            value: x.optionCode,
          })
        )
        this.storerOptions.sort((a, b) => a.label.localeCompare(b.label));
      },
    });

    this.facilityService.getAllVendorOptions().subscribe({
      next: (result: { optionCode: string, optionDesc: string, }[]) => {
        this.vendorOptions = result.map((x) => ({
          label: `${x.optionCode}-${x.optionDesc}`,
          value: x.optionCode,
        })
        );

        // 設定預設值為第一個選項
        if (this.vendorOptions.length > 0) {
          this.searchForm.patchValue({
            vendorList: [this.vendorOptions[0].value]
          });
        }
      }
    });

    this.dropdownOptionsService.getBrandOptions(1)
      .subscribe({
        next: (options: { brandName: string }[]) => {
          this.brandOptions = options.map(x => ({ label: x.brandName, value: x.brandName }));
        }
      });

    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'AmbientStorage').subscribe({
      next: (result: { codeKey: string, codeDesc: string }[]) => {
        this.ambientStorageOptions = [...result].map(x => {
          return {
            label: x.codeDesc,
            value: x.codeKey,
          };
        });
      }
    });


    this.commonDataService.getCodeConverter(this.laasSetting.language, 'DropdownList', 'advising')
    .subscribe({
      next: (results: CodeConverterOptions[]) => {
        this.docStatusOptions = results.map((x) => ({
          label: x.codeDesc,
          value: x.codeKey,
        }));

        this.translateService.get('Common.Message.ChooseOption').subscribe({
          next: (msg) => {
            this.docStatusOptions.unshift({
              label: msg,
              value: '',
            });
          },
        });
      },
    });

  }

  // click sort emit
  tableSort(event: any): void {
    this.sortOrder = { property: event.field, direction: event.order };
    this.searchTable.setCurrentPage(0);
    this.searchVMI();
  }

  // change page emit
  tablePageChange(event: any) {
    this.pageInfo.pageSize = event.rows;
    this.pageInfo.pageIndex = event.first / this.pageInfo.pageSize;
    this.searchVMI();
  }

  resetClickBtn(): void {
    this.searchForm.reset({
      facilityList: [this.laasSetting.facility],
      vendorList: this.vendorOptions.length > 0 ? [this.vendorOptions[0].value] : []
    }
    );
  }

  searchVMI() {
    // 驗證必填欄位
    if (this.searchForm.invalid) {
      this.searchForm.markAllAsTouched();
      this.toastService.warn(this.translateService.instant('Common.Toast.CheckRequiredFields'));
      return;
    }

    const req = Object.assign({ ... this.searchForm.value }, {
      pageIndex: this.pageInfo.pageIndex,
      pageSize: this.pageInfo.pageSize,
      sortOrder: this.sortOrder
    });

    // call search
    this.tableLoading = true;
    this.advisingService.searchVMIAdvising(req).subscribe({
      next: resp => {
        this.searchResults = resp.content;
        this.searchTable.setPageInfo(resp.totalElements);
        this.tableLoading = false;
      },
      error: resp => {
        this.tableLoading = false;
        if (resp.error) {
          this.toastErrorMessage(resp.error);
        }
      },
      complete: () => {
      },
    });
  }

  searchDetailClick(event: any): void {
    const isClosed = event.expanded;
    if (isClosed) {
      return;
    }

    // call api
    const seqNo = event.rowData.seqNo;
    const rowData = event.rowData;
    this.tableLoading = true;
    this.advisingService.searchVMIAdvisingDetail({ mainSeqNo: seqNo }).subscribe({
      next: resp => {
        rowData.details = resp;
        this.tableLoading = false;
      },
      error: resp => {
        this.tableLoading = false;
        if (resp.error) {
          this.toastErrorMessage(resp.error);
        }
      },
      complete: () => {

      },
    });
  }

  // 上傳、下載
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
      input.value = '';
    }
  }
  uploadFile() {
    this.uploadLoading = true;
    this.advisingService.uploadVMIAdFile(this.selectedFile).subscribe({
      next: resp => {
        this.toastService.success(this.translateService.instant('VMIAdVising.Message.UploadSuccess'));
      },
      error: resp => {
        if (resp.error) {
          this.toastErrorMessage(resp.error);
        }
        this.showUploadAdModal = false;
        this.selectedFile = null;
        this.uploadLoading = false;
      },
      complete: () => {
        this.showUploadAdModal = false;
        this.selectedFile = null;
        this.uploadLoading = false;
      },
    })
  }

  downloadTemplate() {
    const fileType = 'VMIAdCreateTemplate';
    this.commonDataService.getSystemParam(fileType).subscribe({
      next: result => {
        const searchObj = {
          sn: result[0].sn,
          version: result[0].version
        };

        this.doService.downloadFile(searchObj).subscribe(x => {
          const blob = new Blob([x], { type: 'application/octet-stream' });
          const downloadURL = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = downloadURL;
          link.download = 'VMIAdCreateTemplate.xlsx';
          link.click();
        });
      }
    });
  }

  closedUpploadModal() {
    this.selectedFile = null;
  }

}
